import React, { useState } from 'react';
import './HorizonPicker.css';
import { filterPickerTasks, defaultLaneFor } from './horizonPlan';
import UserAvatar from './UserAvatar';

/**
 * WR-024 B2: the Add-tasks picker for a Horizon.
 * It only chooses; the page posts the entries (server defaults each lane from the assignee).
 *
 * Props: { horizon, project, tasks, onAdd, onClose }
 */
function HorizonPicker({ horizon, project, tasks, onAdd, onClose }) {
  const [query, setQuery] = useState('');
  const [columnId, setColumnId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [tagId, setTagId] = useState('');
  const [status, setStatus] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  //Exclude tasks already on horizon and archived tasks
  const excludedTaskIds = (horizon.entries || []).map(e => e.task);
  const excludeSet = new Set(excludedTaskIds);

  const filters = { query, columnId, assigneeId, tagId, status };
  const shownTasks = filterPickerTasks(tasks, filters, excludeSet);

  // Build lane map for lane lookups
  const laneMap = new Map();
  (horizon.lanes || []).forEach(lane => {
    laneMap.set(String(lane._id || lane.id), lane);
  });

  // Get lane display: { swatchClass, name } or null
  function getLane(task) {
    const laneId = defaultLaneFor(horizon, task);
    if (!laneId) return null;
    const lane = laneMap.get(laneId);
    if (!lane) return null;
    const color = lane.color || 'none';
    return { swatchClass: `hz-swatch hz-lane-${color}`, name: lane.name };
  }

  // Update selection for a task
  function toggleSelection(taskId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }

  // Select all shown tasks
  function handleSelectAll() {
    if (shownTasks.length === 0) return;
    const allShownSelected = shownTasks.every(t => selectedIds.has(t._id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allShownSelected) {
        shownTasks.forEach(t => next.delete(t._id));
      } else {
        shownTasks.forEach(t => next.add(t._id));
      }
      return next;
    });
  }

  // Check if all shown tasks are selected
  const allShownSelected = shownTasks.length > 0 && shownTasks.every(t => selectedIds.has(t._id));

  // Count selected (all selected, not just shown)
  const selectedCount = selectedIds.size;
  const shownCount = shownTasks.length;

  // Format status text
  function formatStatus(s) {
    if (s === 'todo') return 'To do';
    if (s === 'inprogress') return 'In progress';
    if (s === 'done') return 'Done';
    return '';
  }

  // Handle Add button click
  async function handleAdd() {
    if (selectedCount === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Return task ids in board order (order of tasks prop)
      const idsInBoardOrder = tasks
        .filter(t => selectedIds.has(t._id))
        .map(t => t._id);
      await onAdd(idsInBoardOrder);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  // Project members list: owner first, then members without duplicates
  const members = [];
  const memberIds = new Set();
  if (project.owner) {
    members.push(project.owner);
    memberIds.add(String(project.owner._id));
  }
  if (project.members) {
    project.members.forEach(m => {
      if (!memberIds.has(String(m._id))) {
        members.push(m);
        memberIds.add(String(m._id));
      }
    });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content hzp" onClick={e => e.stopPropagation()}>
        <div className="hzp-header">
          <h2>Add tasks to {horizon.name}</h2>
          <button type="button" className="hzp-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="hzp-body">
          {/* Search */}
          <input
            type="text"
            className="hzp-search"
            placeholder="Search title or BST id"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />

          {/* Filters */}
          <div className="hzp-filters">
            {/* Column filter */}
            <select
              className="hzp-filter"
              data-filter="column"
              value={columnId}
              onChange={e => setColumnId(e.target.value)}
            >
              <option value="">All columns</option>
              {project.columns &&
                project.columns.map(col => (
                  <option key={col.id} value={col.id}>
                    {col.name}
                  </option>
                ))}
            </select>

            {/* Assignee filter */}
            <select
              className="hzp-filter"
              data-filter="assignee"
              value={assigneeId}
              onChange={e => setAssigneeId(e.target.value)}
            >
              <option value="">Anyone</option>
              <option value="unassigned">Unassigned</option>
              {members.map(m => (
                <option key={m._id} value={m._id}>
                  {m.name}
                </option>
              ))}
            </select>

            {/* Tag filter */}
            <select
              className="hzp-filter"
              data-filter="tag"
              value={tagId}
              onChange={e => setTagId(e.target.value)}
            >
              <option value="">All tags</option>
              {project.tags &&
                project.tags.map(tag => (
                  <option key={tag._id} value={tag._id}>
                    {tag.name}
                  </option>
                ))}
            </select>

            {/* Status filter */}
            <select
              className="hzp-filter"
              data-filter="status"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="">Any status</option>
              <option value="todo">To do</option>
              <option value="inprogress">In progress</option>
              <option value="done">Done</option>
            </select>
          </div>

          {/* Selection bar */}
          <div className="hzp-bar">
            <label className="hzp-select-all-label">
              <input
                type="checkbox"
                className="hzp-select-all"
                checked={allShownSelected}
                onChange={handleSelectAll}
              />
              Select all shown
            </label>
            <span className="hzp-count">
              {shownCount} shown · {selectedCount} selected
            </span>
          </div>

          {/* Task list */}
          <div className="hzp-list">
            {shownTasks.length === 0 ? (
              <div className="hzp-empty">No tasks to add.</div>
            ) : (
              shownTasks.map(task => {
                const lane = getLane(task);
                const firstAssignee =
                  task.assignedTo && task.assignedTo.length > 0
                    ? task.assignedTo[0]
                    : null;

                return (
                  <label key={task._id} className="hzp-row" data-task-id={task._id}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(task._id)}
                      onChange={() => toggleSelection(task._id)}
                    />
                    <span className="hzp-bst">{task.bstId}</span>
                    <span className="hzp-title">{task.title}</span>
                    <span className="hzp-status">{formatStatus(task.status)}</span>
                    {firstAssignee && (
                      <UserAvatar
                        user={firstAssignee}
                        className="hzp-avatar"
                      />
                    )}
                    {lane ? (
                      <span className="hzp-lane">
                        <span className={lane.swatchClass}></span>{' '}
                        {lane.name}
                      </span>
                    ) : (
                      <span className="hzp-lane">No lane</span>
                    )}
                  </label>
                );
              })
            )}
          </div>

          {/* Error */}
          {error && <div className="hzp-error">{error}</div>}
        </div>

        {/* Footer */}
        <div className="hzp-footer">
          <div className="hzp-note">
            Lane defaults from the task's assignee; change it per row afterwards.
          </div>
          <button className="hzp-cancel btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="hzp-add btn btn-primary"
            onClick={handleAdd}
            disabled={selectedCount === 0 || busy}
          >
            {busy ? 'Adding...' : selectedCount === 1 ? 'Add 1 task' : `Add ${selectedCount} tasks`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default HorizonPicker;
